import React from 'react'
import SimpleReactLightbox, { SRLWrapper } from 'simple-react-lightbox'

import styled, { css } from 'styled-components'

import { ScrollRevealItem } from 'components/shared/ScrollReveal'

const StyledProjectGrid = styled.section`
  position: relative;
`

const rowGap = css`
  @media (min-width: 576px) {
    gap: 10px;
  }
  @media (max-width: 575px) {
    gap: 2px;
  }
`

const ImageContainer = styled.div``

const GalleryRows = styled.div`
  display: flex;
  flex-direction: column;
  ${rowGap}
`

const GalleryRow = styled.div<{ $columns: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.$columns};

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }

  ${rowGap}
`

const GridImage = styled.img<{
  $isPortrait?: boolean
  /** One image on last row uses a 1-column grid — no span needed */
  $singleColumnRow?: boolean
  /** Two images share a row (e.g. 4-up layout) — do not span full width */
  $pairedRow?: boolean
}>`
  ${(props) =>
    !props.$singleColumnRow &&
    !props.$isPortrait &&
    !props.$pairedRow &&
    css`
      grid-column: span 2;
    `}
  object-fit: cover !important;
  &:hover {
    cursor: pointer;
    opacity: 0.9;
  }
  max-height: 600px;
  @media (max-width: 767px) {
    max-height: 200px;
  }
`

interface TextImageGalleryProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_Text
}

type GalleryImage = NonNullable<
  NonNullable<TextImageGalleryProps['fields']['images']>
>[number]

/** Pair images two per row in order; odd last image gets its own row (no orientation logic). */
function buildImageRows(images: GalleryImage[]): GalleryImage[][] {
  const rows: GalleryImage[][] = []
  let i = 0
  while (i < images.length) {
    const next = images[i + 1]
    if (next) {
      rows.push([images[i], next])
      i += 2
    } else {
      rows.push([images[i]])
      i += 1
    }
  }
  return rows
}

/** Rows 3, 6, 9… (1-based) → 1fr 2fr; others alternate 2fr 1fr / 1fr 1fr */
function getRowTemplate(rowIdx: number): string {
  if (rowIdx % 3 === 2) {
    return '1fr 2fr'
  }
  return rowIdx % 2 === 0 ? '2fr 1fr' : '1fr 1fr'
}

function TextImageGallery({ fields }: TextImageGalleryProps) {
  const options = {}
  const images = (fields.images ?? []) as GalleryImage[]
  /** Four images: fixed 2×2 order; otherwise buildImageRows pairs consecutive images. */
  const rows =
    images.length === 4
      ? [
          [images[0], images[1]],
          [images[2], images[3]],
        ]
      : buildImageRows(images)
  const isFourImageGallery = images.length === 4

  if (fields?.images?.length === 2) {
    return (
      <SimpleReactLightbox>
        <StyledProjectGrid className="position-relative container mb-5">
          <ImageContainer>
            <SRLWrapper options={options}>
              <div className="row">
                <ScrollRevealItem className="col-lg-6" sequenceIndex={0}>
                  <GridImage
                    loading="lazy"
                    className="w-100 h-100"
                    src={fields?.images?.[0]?.localFile?.publicURL ?? ''}
                    $isPortrait
                  />
                </ScrollRevealItem>
                <ScrollRevealItem className="col-lg-6" sequenceIndex={1}>
                  <GridImage
                    loading="lazy"
                    className="w-100 h-100"
                    src={fields?.images?.[1]?.localFile?.publicURL ?? ''}
                    $isPortrait
                  />
                </ScrollRevealItem>
              </div>
            </SRLWrapper>
          </ImageContainer>
        </StyledProjectGrid>
      </SimpleReactLightbox>
    )
  }

  return (
    <SimpleReactLightbox>
      <StyledProjectGrid className="position-relative container mb-5">
        <ImageContainer>
          <SRLWrapper options={options}>
            <GalleryRows>
              {rows.map((rowImages, rowIdx) => {
                const isLastRowSingle =
                  rowImages.length === 1 && rowIdx === rows.length - 1
                let columns = getRowTemplate(rowIdx)
                if (isFourImageGallery) {
                  columns = rowIdx === 0 ? '2fr 1fr' : '1fr 2fr'
                } else if (isLastRowSingle) {
                  columns = '1fr'
                }
                const rowKey = rowImages
                  .map((img) => img?.localFile?.publicURL ?? '')
                  .join('|')
                return (
                  <GalleryRow key={rowKey} $columns={columns}>
                    {rowImages.map((image) => (
                      <GridImage
                        loading="lazy"
                        className="w-100 h-100"
                        key={
                          image?.localFile?.publicURL ??
                          `missing-${rowImages.indexOf(image)}`
                        }
                        src={image?.localFile?.publicURL ?? ''}
                        $isPortrait={rowImages.length === 1}
                        $singleColumnRow={isLastRowSingle}
                        $pairedRow={rowImages.length === 2}
                      />
                    ))}
                  </GalleryRow>
                )
              })}
            </GalleryRows>
          </SRLWrapper>
        </ImageContainer>
      </StyledProjectGrid>
    </SimpleReactLightbox>
  )
}

export default TextImageGallery
